import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import css from './OfferBanner.module.scss';

interface OfferBannerProps {
  // A single icon element (e.g. <MailOutlined />) — the banner styles it.
  icon: React.ReactElement;
  // The offer copy; rendered before the inline CTA.
  children: React.ReactNode;
  ctaLabel: React.ReactNode;
  onCta: () => void;
  dismissAriaLabel?: string;
  onDismiss: () => void;
}

/**
 * The site's "quiet offer above a table" banner: white card with a green
 * accent edge, an outline action button and a dismiss X. Purely presentational
 * — consumers own their visibility conditions, dismiss persistence and
 * analytics.
 *
 * The action is a real button, not text with an arrow: it opens a modal in
 * place, so an arrow promised navigation that never happens, and it shares
 * this slot with WidenSearchStrip, whose action has always been a button.
 */
const OfferBanner: React.FC<OfferBannerProps> = ({
  icon,
  children,
  ctaLabel,
  onCta,
  dismissAriaLabel = 'Dismiss',
  onDismiss,
}) => (
  <div className={css.banner} role="status">
    {React.cloneElement(icon, { className: css.icon, 'aria-hidden': 'true' })}
    <div className={css.text}>{children}</div>
    {/* Its own flex child rather than inline in the copy, so it can't end up
        stranded mid-sentence on a wrap. */}
    <button type="button" className={css.cta} onClick={onCta}>
      {ctaLabel}
    </button>
    <button
      type="button"
      className={css.dismiss}
      onClick={onDismiss}
      aria-label={dismissAriaLabel}
    >
      <CloseOutlined />
    </button>
  </div>
);

export default OfferBanner;
